<template>
  <div>
    <!--    기본정보-->
    <field-select-module
      class="mt-28px"
      title="Basic Information"
      v-model="form.stdInfo"
      :board="this.board"
      :is-print="isPrint"
      field-name="Company"
      :typeslabel="fieldTypesLabel"
    />

    <!-- Risk Assessment Information -->
    <div class="mt-8">
      <h5 class="my-4"><i class="dot"></i> Risk Assessment Information</h5>
      <div class="writeGridContainer">
        <div>
          <div>Operation / Work activity being assessed</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.riskInfo.operation }}</p>
          <ml-input v-else :readonly="board.readonly" v-model="form.riskInfo.operation" />
        </div>
        <div>
          <radio-group flex :items="['Routine', 'Non-routine']" v-model="form.riskInfo.routine" :readonly="isPrint || board.readonly" class="w-full" />
        </div>
        <div></div>
        <div>
          <div>Generated by</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.riskInfo.generated }}</p>
          <ml-input v-else :readonly="board.readonly" v-model="form.riskInfo.generated" />
        </div>
        <div>
          <radio-group flex :items="['(record the name)', 'Office']" v-model="form.riskInfo.office" :readonly="isPrint || board.readonly" class="w-full" />
        </div>
        <div></div>
        <div>
          <div>Code number (to be assigned by the Office)</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.riskInfo.code }}</p>
          <ml-input v-else :readonly="board.readonly" v-model="form.riskInfo.code" />
        </div>
      </div>
    </div>

    <div class="mt-8">
      <div class="flex justify-between items-center">
        <div><h5 class="my-4"><i class="dot"></i> Risk Assessment</h5></div>
        <div class="ml-auto">
          <vs-button color="secondary" @click="onClickAddItem()"> Add item </vs-button>
          <vs-button color="secondary" type="border" class="ml-2" @click="onClickDeleteSelection" :disabled="selectedAssessmentIds.length === 0"> Delete selection </vs-button>
        </div>
      </div>

      <table class="w-full mt-2 risk-table">
        <thead>
        <tr>
          <th rowspan="2" style="width: 1%"><vs-checkbox :checked="selectedAssessmentAll" class="i-check justify-center" @change="onClickAssessmentAll"/></th>
          <th rowspan="2">No.</th>
          <th colspan="2">Risk Identification</th>
          <th>Risk Analysis</th>
          <th colspan="3">Initial risk evaluation</th>
        </tr>
        <tr>
          <th style="width: 26%">Hazard</th>
          <th style="width: 12.8%">Consequence</th>
          <th style="width: 38.8%">Existing control measures</th>
          <th class="column-frequency">S</th>
          <th class="column-frequency">L</th>
          <th class="column-frequency">R</th>
        </tr>
        </thead>

        <tbody>
        <tr v-for="(row, idx) of form.assessments" :key="idx">
          <td>
            <vs-checkbox class="i-check justify-center"
                         :checked="selectedAssessmentIds.includes(row.id)"
                         @change="onClickAssessmentItem($event, row.id)"/>
          </td>
          <td>{{ idx + 1 }}</td>
          <td><ml-textarea rows="5" v-model="row.riskHazard" :is-print="isPrint" /></td>
          <td><ml-textarea rows="5" v-model="row.riskConsequence" :is-print="isPrint" /></td>
          <td><ml-textarea rows="5" v-model="row.existingControl" :is-print="isPrint" /></td>
          <td>
            <p v-if="isPrint" class="print-input">{{ row.initialRiskS }}</p>
            <vs-select :disabled="board.readonly" v-else v-model="row.initialRiskS" autocomplete class="w-full flex-1">
              <vs-select-item text="-" :value="0" />
              <vs-select-item v-for="idx in 5" :text="idx" :value="idx" :key="idx" />
            </vs-select>
          </td>
          <td>
            <p v-if="isPrint" class="print-input">{{ row.initialRiskL }}</p>
            <vs-select :disabled="board.readonly" v-else v-model="row.initialRiskL" autocomplete class="w-full flex-1">
              <vs-select-item text="-" :value="0" />
              <vs-select-item v-for="idx in 5" :text="idx" :value="idx" :key="idx" />
            </vs-select>
          </td>
          <td><ml-input v-model="row.initialRiskR" align="center" readonly style="width: 60px;" :style="{color: getInitialRiskRColor(row.initialRiskS, row.initialRiskL)}" /></td>
        </tr>
        </tbody>
      </table>
    </div>
    <p class="mt-2">※ S: Severity, L: Likelihood, R: Risk</p>


    <div v-if="form.assessments.length > 0" class="mt-8">
      <div class="flex justify-between items-center">
        <div><h5 class="my-4"><i class="dot"></i> Risk Treatment</h5></div>
        <div class="ml-auto">
          <vs-button color="secondary" @click="onClickAddTreatmentItem()"> Add item </vs-button>
          <vs-button color="secondary" type="border" class="ml-2" @click="onClickDeleteTreatmentSelection" :disabled="selectedTreatmentIds.length === 0"> Delete selection </vs-button>
        </div>
      </div>

      <table class="w-full mt-2 risk-table">
        <thead>
        <tr>
          <th rowspan="2" style="width: 1%"><vs-checkbox :checked="selectedTreatmentAll" class="i-check justify-center" @change="onClickTreatmentAll"/></th>
          <th rowspan="2" style="width: 80px;">Hazard No.</th>
          <th colspan="3">Risk Treatment</th>
          <th colspan="3">Residual risk evaluation</th>
        </tr>
        <tr>
          <th>Additional Risk Control Measures</th>
          <th style="width: 18%">Responsible</th>
          <th style="width: 18%">Action Timeline</th>
          <th class="column-frequency">S</th>
          <th class="column-frequency">L</th>
          <th class="column-frequency">R</th>
        </tr>
        </thead>

        <tbody>
        <tr v-for="(row, idx) of form.treatments" :key="idx">
          <td>
            <vs-checkbox class="i-check justify-center"
                         :checked="selectedTreatmentIds.includes(row.id)"
                         @change="onClickTreatmentItem($event, row.id)"/>
          </td>
          <td><number-input v-model="row.hazardNo" align="center" :min="1" :max="form.assessments.length || 0"  /></td>
          <td><ml-textarea rows="5" v-model="row.additionalRiskControl" :is-print="isPrint" /></td>
          <td><ml-textarea rows="5" v-model="row.responsible" :is-print="isPrint" /></td>
          <td><ml-textarea rows="5" v-model="row.actionTimeline" :is-print="isPrint" /></td>
          <td>
            <p v-if="isPrint" class="print-input">{{ row.residualRiskS }}</p>
            <vs-select :disabled="board.readonly" v-else v-model="row.residualRiskS" autocomplete class="w-full flex-1">
              <vs-select-item text="-" :value="0" />
              <vs-select-item v-for="idx in 5" :text="idx" :value="idx" :key="idx" />
            </vs-select>
          </td>
          <td>
            <p v-if="isPrint" class="print-input">{{ row.residualRiskL }}</p>
            <vs-select :disabled="board.readonly" v-else v-model="row.residualRiskL" autocomplete class="w-full flex-1">
              <vs-select-item text="-" :value="0" />
              <vs-select-item v-for="idx in 5" :text="idx" :value="idx" :key="idx" />
            </vs-select>
          </td>
          <td><ml-input v-model="row.residualRiskR" align="center" readonly style="width: 60px;" :style="{color: getInitialRiskRColor(row.residualRiskS, row.residualRiskL)}" /></td>
        </tr>
        </tbody>
      </table>
    </div>
    <p class="mt-2">※ S: Severity, L: Likelihood, R: Risk</p>

    <div class="mt-8">
      <table class="w-full risk-table">
        <thead>
        <tr>
          <th style="text-align: left; padding-left: 15px;">ALTERNATIVE WAYS TO CONDUCT THE WORK(a new RA    should be conducted for the alternative way decided)</th>
        </tr>
        </thead>

        <tbody>
        <tr>
          <td><ml-textarea rows="3" v-model="form.alternativeWays" :is-print="isPrint" /></td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-8">
      <table class="w-full risk-table">
        <thead>
        <tr>
          <th colspan="2" style="text-align: left; padding-left: 15px;">Are any amendments to SHEQ required (related to    the above Additional Risk Control Measures)?</th>
        </tr>
        </thead>

        <tbody>
        <tr>
          <td style="width: 70px;">
            <radio-group flex :items="['Yes', 'No']" v-model="form.sheqRequired" :readonly="isPrint || board.readonly" class="w-full" />
          </td>
          <td class="flex" style="border-left: none; border-top: none;">
            <div style="padding-top: 10px; padding-right: 10px;">Describe:</div>
            <div style="width: 100%"><ml-textarea rows="1" v-model="form.sheqDescribe" :is-print="isPrint" /></div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-8">
      <table class="w-full risk-table">
        <thead>
        <tr>
          <th colspan="2" style="text-align: left; padding-left: 15px;">Contingency plans (to facilitate safe management and recovery of the situation in case of any unplanned occurrences)</th>
        </tr>
        </thead>

        <tbody>
        <tr>
          <td style="width: 160px; text-align: left;">Emergency case</td>
          <td><ml-input v-model="form.emergencyCase" :is-print="isPrint" class="w-full text-left" placeholder="Injury" /></td>
        </tr>
        <tr>
          <td>Contingency plans</td>
          <td><ml-input v-model="form.contingencyPlans" :is-print="isPrint" class="w-full text-left" placeholder="REFER TO SMMC-ERP Chapter 5" /></td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Risk Assessment completed by -->
    <div class="mt-8">
      <table class="w-full risk-table">
        <thead>
        <tr>
          <th colspan="5" style="text-align: left; padding-left: 15px;">Risk Assessment completed by</th>
        </tr>
        <tr>
          <th colspan="2">Name</th>
          <th>Rank</th>
          <th>Signature</th>
          <th>Date</th>
        </tr>
        </thead>

        <tbody>
        <tr>
          <td colspan="2" style="width: 25%;">
            <ml-input v-model="form.signatures.completed.name" :is-print="isPrint" class="w-full text-left" />
          </td>
          <td style="width: 25%;">
            <ml-input v-model="form.signatures.completed.rank" :is-print="isPrint" class="w-full text-left" />
          </td>
          <td style="width: 25%;">
            <sign-field v-if="!isPrint && !board.readonly" lang="en" v-model="form.signatures.completed.signature" class="w-full flex-1 p-1 signField" />
            <div v-else class="w-full flex-1 p-1">
              <img v-if="form.sign3.sign" :src="form.signatures.completed.signature" width="100%" alt="Signature" class="signatureImg" />
              <div v-else class="signatureImg"></div>
            </div>
          </td>
          <td>
            <p v-if="isPrint" class="print-input flex-1">{{ form.signatures.completed.date }}</p>
            <date-selector v-else :readonly="board.readonly" v-model="form.signatures.completed.date" class="w-full flex-1" />
          </td>
        </tr>
        <tr>
          <td style="width: 10%; border-right: none;">Remarks: </td>
          <td colspan="4" style="border-left: none;">
            <ml-input v-model="form.signatures.completed.remarks" :is-print="isPrint" class="w-full text-left" />
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Worker (or representative worker) -->
    <div class="mt-8">
      <table class="w-full risk-table">
        <thead>
        <tr>
          <th colspan="5" style="text-align: left; padding-left: 15px;">Worker (or representative worker)</th>
        </tr>
        <tr>
          <th colspan="2">Name</th>
          <th>Rank</th>
          <th>Signature</th>
          <th>Date</th>
        </tr>
        </thead>

        <tbody>
        <tr>
          <td colspan="2" style="width: 25%;">
            <ml-input v-model="form.signatures.worker.name" :is-print="isPrint" class="w-full text-left" />
          </td>
          <td style="width: 25%;">
            <ml-input v-model="form.signatures.worker.rank" :is-print="isPrint" class="w-full text-left" />
          </td>
          <td style="width: 25%;">
            <sign-field v-if="!isPrint && !board.readonly" lang="en" v-model="form.signatures.worker.signature" class="w-full flex-1 p-1 signField" />
            <div v-else class="w-full flex-1 p-1">
              <img v-if="form.sign3.sign" :src="form.signatures.worker.signature" width="100%" alt="Signature" class="signatureImg" />
              <div v-else class="signatureImg"></div>
            </div>
          </td>
          <td>
            <p v-if="isPrint" class="print-input flex-1">{{ form.signatures.worker.date }}</p>
            <date-selector v-else :readonly="board.readonly" v-model="form.signatures.worker.date" class="w-full flex-1" />
          </td>
        </tr>
        <tr>
          <td style="width: 10%; border-right: none;">Remarks: </td>
          <td colspan="4" style="border-left: none;">
            <ml-input v-model="form.signatures.worker.remarks" :is-print="isPrint" class="w-full text-left" />
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Risk Assessment reviewed by -->
    <div class="mt-8">
      <table class="w-full risk-table">
        <thead>
        <tr>
          <th colspan="5" style="text-align: left; padding-left: 15px;">Risk Assessment reviewed by</th>
        </tr>
        <tr>
          <th colspan="2">Name</th>
          <th>Rank</th>
          <th>Signature</th>
          <th>Date</th>
        </tr>
        </thead>

        <tbody>
        <tr>
          <td colspan="2" style="width: 25%;">
            <ml-input v-model="form.signatures.reviewed.name" :is-print="isPrint" class="w-full text-left" />
          </td>
          <td style="width: 25%;">
            <ml-input v-model="form.signatures.reviewed.rank" :is-print="isPrint" class="w-full text-left" />
          </td>
          <td style="width: 25%;">
            <sign-field v-if="!isPrint && !board.readonly" lang="en" v-model="form.signatures.reviewed.signature" class="w-full flex-1 p-1 signField" />
            <div v-else class="w-full flex-1 p-1">
              <img v-if="form.sign3.sign" :src="form.signatures.reviewed.signature" width="100%" alt="Signature" class="signatureImg" />
              <div v-else class="signatureImg"></div>
            </div>
          </td>
          <td>
            <p v-if="isPrint" class="print-input flex-1">{{ form.signatures.reviewed.date }}</p>
            <date-selector v-else :readonly="board.readonly" v-model="form.signatures.reviewed.date" class="w-full flex-1" />
          </td>
        </tr>
        <tr>
          <td style="width: 10%; border-right: none;">Remarks: </td>
          <td colspan="4" style="border-left: none;">
            <ml-input v-model="form.signatures.reviewed.remarks" :is-print="isPrint" class="w-full text-left" />
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Risk Assessment approved by: -->
    <div class="mt-8">
      <table class="w-full risk-table">
        <thead>
        <tr>
          <th colspan="5" style="text-align: left; padding-left: 15px;">Risk Assessment approved by</th>
        </tr>
        <tr>
          <th colspan="2">Name</th>
          <th>Rank</th>
          <th>Signature</th>
          <th>Date</th>
        </tr>
        </thead>

        <tbody>
        <tr>
          <td colspan="2" style="width: 25%;">
            <ml-input v-model="form.signatures.approved.name" :is-print="isPrint" class="w-full text-left" />
          </td>
          <td style="width: 25%;">
            <ml-input v-model="form.signatures.approved.rank" :is-print="isPrint" class="w-full text-left" />
          </td>
          <td style="width: 25%;">
            <sign-field v-if="!isPrint && !board.readonly" lang="en" v-model="form.signatures.approved.signature" class="w-full flex-1 p-1 signField" />
            <div v-else class="w-full flex-1 p-1">
              <img v-if="form.sign3.sign" :src="form.signatures.approved.signature" width="100%" alt="Signature" class="signatureImg" />
              <div v-else class="signatureImg"></div>
            </div>
          </td>
          <td>
            <p v-if="isPrint" class="print-input flex-1">{{ form.signatures.approved.date }}</p>
            <date-selector v-else :readonly="board.readonly" v-model="form.signatures.approved.date" class="w-full flex-1" />
          </td>
        </tr>
        <tr>
          <td style="width: 10%; border-right: none;">Remarks: </td>
          <td colspan="4" style="border-left: none;">
            <ml-input v-model="form.signatures.approved.remarks" :is-print="isPrint" class="w-full text-left" />
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <p class="mt-8">NB. If the Residual Risk is Medium (Substantial) or High, the work should not be carried out until office approval is taken</p>
    <p class="mt-2">NB. All Non Routine jobs RA have to be sent for the office approval prior commencement of the job</p>
    <p class="mt-2">NB. All Routine jobs not listed in the RA library have to be sent for office approval prior commencement of the job</p>
    <p class="mt-2">NB. This form is to be completed whenever the Initial risk is found to be Medium (Substantial) or High</p>


    <!--    파일첨부-->
    <file-upload-module id="fileList" title="Attachments" lang="en" class="w-full mt-4" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly"></file-upload-module>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FileUploadModule from "@/views/modules/FileUploadModule";
import TableInspectMerge from "@/components/TableInspectMerge";
import DateSelector from "@/components/selector/DateSelector";
import DisasterClassSelector from "@/components/selector/DisasterClassSelector";
import CompanySelector from "@/components/selector/CompanySelector";
import SignField from "@/components/SignField.vue";
import NameWithSignatureRow from "@/components/NameWithSignatureRow.vue";
import Pagination from "@/components/Pagination.vue";
import Print from "./Print.vue";
import TimeSelector from "@/components/selector/TimeSelector.vue";
import NumberInput from "@/components/NumberInput.vue";
import RadioGroup from "@/components/radio/RadioGroup.vue";

export default {
  components: {
    RadioGroup,
    NumberInput,
    TimeSelector,
    Print,
    Pagination,
    NameWithSignatureRow,
    SignField,
    FieldSelectModule,
    FileUploadModule,
    TableInspectMerge,
    DateSelector,
    DisasterClassSelector,
    CompanySelector,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  data() {
    return {
      form: {
        stdInfo: {},
        riskInfo: {
          operation: "",  // Operation / Work activity being assessed
          routine: "",    // Routine / Non-routine
          generated: "",  // Generated by
          office: "",     // Office
          code: "",       // Code number (to be assigned by the Office)
        },

        assessments: [
          {
            id: "default_item",
            riskHazard: "",
            riskConsequence: "",
            existingControl: "",
            initialRiskS: 0,
            initialRiskL: 0,
            initialRiskR: "",
          }
        ],
        treatments: [
          {
            id: "default_item",
            hazardNo: 1,
            additionalRiskControl: "",
            responsible: "",
            actionTimeline: "",
            residualRiskS: 0,
            residualRiskL: 0,
            residualRiskR: "",
          }
        ],
        alternativeWays: "",
        sheqRequired: "",
        sheqDescribe: "",
        emergencyCase: "",
        contingencyPlans: "",

        signatures: {
          completed : { name: "", date: "", rank: "", signature: "", remarks: "" },
          worker: { name: "", date: "", rank: "", signature: "", remarks: "" },
          reviewed: { name: "", date: "", rank: "", signature: "", remarks: "" },
          approved: { name: "", date: "", rank: "", signature: "", remarks: "" },
        },

        fileList: [],
      },
      selectedAssessmentAll: false,
      selectedTreatmentAll: false,
      // Risk Assessment 테이블에서 선택된 항목의 인덱스를 저장하는 배열
      selectedAssessmentIds: [],
      selectedTreatmentIds: [],
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        // assessements 배열의 initialRiskR 값을 업데이트한다
        value.assessments.forEach((item) => {
          item.initialRiskR = this.getInitialRiskRText(item.initialRiskS, item.initialRiskL);
        });

        // treatments 배열의 residualRiskR 값을 업데이트한다
        value.treatments.forEach((item) => {
          item.residualRiskR = this.getInitialRiskRText(item.residualRiskS, item.residualRiskL);
        });

        this.$emit("input", value);
      },
    },
  },
  computed: {
    fieldTypesLabel() {
      return {
        writer_name: "Writer",
        date: "Date",
      };
    }
  },
  methods: {
    onClickAddItem() {
      this.form.assessments.push({
        id: Math.random().toString(36).substring(2, 9),
        step: "",
        equipment: "",
        type: "",
        safeguards: "",
        likelihood: 0,
        level: 0,
        frequency: 0,
        consequence: 0,
        ranking: 0,
        controlRequired: false,
      });
    },
    onClickAddTreatmentItem() {
      this.form.treatments.push({
        id: Math.random().toString(36).substring(2, 9),
        hazardNo: 1,
        additionalRiskControl: "",
        responsible: "",
        actionTimeline: "",
        residualRiskS: 0,
        residualRiskL: 0,
        residualRiskR: "",
      });
    },
    onClickDeleteSelection() {
      const selected = this.form.assessments.filter((item) => this.selectedAssessmentIds.includes(item.id));
      this.form.assessments = this.form.assessments.filter((item) => !selected.includes(item));
      this.selectedAssessmentIds = [];
      this.selectedAssessmentAll = false;

      // 만약 아이템이 모두 삭제됐다면, 1개를 추가한다
      if (this.form.assessments.length === 0) {
        this.onClickAddItem();
      }
    },
    onClickDeleteTreatmentSelection() {
      const selected = this.form.treatments.filter((item) => this.selectedTreatmentIds.includes(item.id));
      this.form.treatments = this.form.treatments.filter((item) => !selected.includes(item));
      this.selectedTreatmentIds = [];
      this.selectedTreatmentAll = false;

      // 만약 아이템이 모두 삭제됐다면, 1개를 추가한다
      if (this.form.treatments.length === 0) {
        this.onClickAddTreatmentItem();
      }
    },
    onClickAssessmentAll(event) {
      const checked = event.target.checked;

      this.selectedAssessmentAll = checked;
      this.selectedAssessmentIds = checked ? this.form.assessments.map((item) => item.id) : [];
    },
    onClickTreatmentAll(event) {
      const checked = event.target.checked;

      this.selectedTreatmentAll = checked;
      this.selectedTreatmentIds = checked ? this.form.treatments.map((item) => item.id) : [];
    },
    onClickAssessmentItem(event, id) {
      const checked = event.target.checked;

      if (checked) {
        this.selectedAssessmentIds.push(id);
      } else {
        this.selectedAssessmentIds = this.selectedAssessmentIds.filter((item) => item !== id);
      }
      // 전체 선택 체크박스의 상태를 변경
      this.selectedAssessmentAll = this.selectedAssessmentIds.length === this.form.assessments.length;
    },
    onClickTreatmentItem(event, id) {
      const checked = event.target.checked;

      if (checked) {
        this.selectedTreatmentIds.push(id);
      } else {
        this.selectedTreatmentIds = this.selectedTreatmentIds.filter((item) => item !== id);
      }
      // 전체 선택 체크박스의 상태를 변경
      this.selectedTreatmentAll = this.selectedTreatmentIds.length === this.form.treatments.length;
    },
    getInitialRiskR(initialRiskS, initialRiskL) {
      const riskS = parseInt(initialRiskS) || 0;
      const riskL = parseInt(initialRiskL) || 0;
      return riskS * riskL;
    },
    getInitialRiskRText(initialRiskS, initialRiskL) {
      const riskR = this.getInitialRiskR(initialRiskS, initialRiskL);

      if (riskR === 0) {
        return '-';
      } else if (riskR <= 8) {
        return `L(${riskR})`;
      } else if (riskR <= 12) {
        return `M(${riskR})`;
      } else {
        return `H(${riskR})`;
      }
    },
    getInitialRiskRColor(initialRiskS, initialRiskL) {
      const riskR = this.getInitialRiskR(initialRiskS, initialRiskL);

      if (riskR === 0) {
        return 'black';
      } else if (riskR <= 8) {
        return '#28C76F';
      } else if (riskR <= 12) {
        return '#FF8510';
      } else {
        return '#E42728';
      }
    },
  },
};
</script>
<style scoped lang="scss">
.container-title {
  line-height: normal;
  word-break: break-word;
}
.risk-table {
  border-collapse: collapse;

  th, td {
    border: 1px solid #cdcdcd;
    padding: 16px 10px;
    text-align: center;
  }
  th {
    background-color: #f9f9f9;
    color: #3c3c3c;
    font-weight: 500;
    font-size: 14px;

    &.column-frequency {
      width: 5.14%; min-width: 80px;
    }
  }
}

.i-check::v-deep .vs-checkbox {
  margin: 0;
}
.signField::v-deep .sign-field {
  max-width: 100%;
}
</style>

